import { createHash } from "node:crypto";
import type { EventFrame } from "@mewbleh/nori-protocol";
import { GatewayClient } from "@mewbleh/nori-gateway";

type McpEvent = { cursor: number; type: string; sessionKey?: string; payload: unknown; ts: number };
type RpcRequest = { jsonrpc: "2.0"; id?: string | number; method: string; params?: Record<string, unknown> };

const TOOLS = [
  { name: "conversations_list", description: "List Nori session-backed conversations.", inputSchema: { type: "object", properties: { limit: { type: "integer", minimum: 1, maximum: 500 }, search: { type: "string" }, channel: { type: "string" }, includeLastMessage: { type: "boolean" } }, additionalProperties: false } },
  { name: "conversation_get", description: "Get one Nori conversation by session key.", inputSchema: { type: "object", properties: { session_key: { type: "string" } }, required: ["session_key"], additionalProperties: false } },
  { name: "messages_read", description: "Read recent transcript messages for one Nori conversation.", inputSchema: { type: "object", properties: { session_key: { type: "string" }, limit: { type: "integer", minimum: 1, maximum: 200 } }, required: ["session_key"], additionalProperties: false } },
  { name: "attachments_fetch", description: "List non-text content blocks from one transcript message.", inputSchema: { type: "object", properties: { session_key: { type: "string" }, message_id: { type: "string" }, limit: { type: "integer", minimum: 1, maximum: 200 } }, required: ["session_key", "message_id"], additionalProperties: false } },
  { name: "events_poll", description: "Poll live Nori Gateway events since a cursor.", inputSchema: { type: "object", properties: { after_cursor: { type: "integer", minimum: 0 }, session_key: { type: "string" }, limit: { type: "integer", minimum: 1, maximum: 200 } }, additionalProperties: false } },
  { name: "events_wait", description: "Wait for the next matching live Nori Gateway event.", inputSchema: { type: "object", properties: { after_cursor: { type: "integer", minimum: 0 }, session_key: { type: "string" }, timeout_ms: { type: "integer", minimum: 1, maximum: 300000 } }, additionalProperties: false } },
  { name: "messages_send", description: "Send text through an existing Nori conversation route after durable operator approval.", inputSchema: { type: "object", properties: { session_key: { type: "string" }, text: { type: "string" } }, required: ["session_key", "text"], additionalProperties: false } },
  { name: "permissions_list_open", description: "List pending Nori approval requests.", inputSchema: { type: "object", properties: {}, additionalProperties: false } },
  { name: "permissions_respond", description: "Allow once or deny one pending Nori approval request.", inputSchema: { type: "object", properties: { id: { type: "string" }, decision: { type: "string", enum: ["allow-once", "deny"] } }, required: ["id", "decision"], additionalProperties: false } },
] as const;

function boundedInteger(value: unknown, fallback: number, minimum: number, maximum: number): number {
  const number = Number(value ?? fallback);
  return Number.isFinite(number) ? Math.max(minimum, Math.min(Math.trunc(number), maximum)) : fallback;
}

function toolResult(value: unknown, isError = false) {
  return { content: [{ type: "text", text: JSON.stringify(value, null, 2) }], structuredContent: value, ...(isError ? { isError: true } : {}) };
}

export class NoriMcpBridge {
  readonly events: McpEvent[] = [];
  #cursor = 0;
  readonly #waiters = new Set<() => void>();

  constructor(readonly client: GatewayClient) {
    client.on("event", (frame: EventFrame) => {
      if (["tick", "connect.challenge"].includes(frame.event)) return;
      const payload = frame.payload as Record<string, unknown> | undefined;
      const event: McpEvent = {
        cursor: ++this.#cursor,
        type: frame.event,
        ...(payload?.sessionKey ? { sessionKey: String(payload.sessionKey) } : {}),
        payload: frame.payload,
        ts: Date.now(),
      };
      this.events.push(event);
      if (this.events.length > 1_000) this.events.splice(0, this.events.length - 1_000);
      for (const wake of this.#waiters) wake();
    });
  }

  #matching(after: number, sessionKey?: string, limit = 20): McpEvent[] {
    return this.events.filter((event) => event.cursor > after && (!sessionKey || event.sessionKey === sessionKey)).slice(0, limit);
  }

  async callTool(name: string, args: Record<string, unknown> = {}): Promise<unknown> {
    switch (name) {
      case "conversations_list": {
        const response = await this.client.request("sessions.list", { limit: boundedInteger(args.limit, 100, 1, 500), ...(args.search ? { search: String(args.search) } : {}) }) as { sessions?: Array<Record<string, unknown>> };
        let conversations = response.sessions ?? [];
        if (args.channel) conversations = conversations.filter((entry) => entry.channel === String(args.channel));
        if (args.includeLastMessage === true) {
          conversations = await Promise.all(conversations.map(async (entry) => {
            const detail = await this.client.request("sessions.get", { key: entry.sessionKey, limit: 1 }) as { messages?: unknown[] };
            return { ...entry, lastMessage: detail.messages?.at(-1) };
          }));
        }
        return toolResult({ conversations });
      }
      case "conversation_get": {
        try { return toolResult({ conversation: (await this.client.request("sessions.get", { key: String(args.session_key), limit: 1 }) as { session?: unknown }).session }); }
        catch (error) { return toolResult({ error: (error as Error).message }, true); }
      }
      case "messages_read": {
        const response = await this.client.request("sessions.get", { key: String(args.session_key), limit: boundedInteger(args.limit, 20, 1, 200) }) as { messages?: unknown[] };
        return toolResult({ messages: response.messages ?? [] });
      }
      case "attachments_fetch": {
        const response = await this.client.request("sessions.get", { key: String(args.session_key), limit: boundedInteger(args.limit, 100, 1, 200) }) as { messages?: Array<Record<string, unknown>> };
        const message = (response.messages ?? []).find((entry) => entry.id === String(args.message_id));
        if (!message) return toolResult({ error: `message not found: ${String(args.message_id)}` }, true);
        const attachments = Array.isArray(message.content) ? message.content.filter((entry) => (entry as { type?: unknown }).type !== "text") : [];
        return toolResult({ attachments, message });
      }
      case "events_poll": {
        const after = boundedInteger(args.after_cursor, 0, 0, Number.MAX_SAFE_INTEGER);
        const events = this.#matching(after, args.session_key ? String(args.session_key) : undefined, boundedInteger(args.limit, 20, 1, 200));
        return toolResult({ events, next_cursor: events.at(-1)?.cursor ?? after });
      }
      case "events_wait": {
        const after = boundedInteger(args.after_cursor, 0, 0, Number.MAX_SAFE_INTEGER);
        const sessionKey = args.session_key ? String(args.session_key) : undefined;
        let event = this.#matching(after, sessionKey, 1)[0];
        if (!event) {
          await new Promise<void>((resolve) => {
            const wake = () => { clearTimeout(timer); this.#waiters.delete(wake); resolve(); };
            const timer = setTimeout(wake, boundedInteger(args.timeout_ms, 30_000, 1, 300_000));
            this.#waiters.add(wake);
          });
          event = this.#matching(after, sessionKey, 1)[0];
        }
        return toolResult({ event: event ?? null });
      }
      case "messages_send": {
        const sessionKey = String(args.session_key);
        const text = String(args.text);
        const idempotencyKey = createHash("sha256").update(`${sessionKey}\0${text}`).digest("hex");
        return toolResult({ result: await this.client.request("message.send", { sessionKey, text, confirm: true, idempotencyKey }) });
      }
      case "permissions_list_open":
        return toolResult(await this.client.request("approvals.list", { status: "pending" }));
      case "permissions_respond": {
        const decision = String(args.decision);
        if (decision !== "allow-once" && decision !== "deny") return toolResult({ error: "decision must be allow-once or deny" }, true);
        return toolResult(await this.client.request("approvals.resolve", { id: String(args.id), decision: decision === "deny" ? "denied" : "approved" }));
      }
      default:
        return toolResult({ error: `unknown tool: ${name}` }, true);
    }
  }

  async handle(request: RpcRequest): Promise<unknown> {
    switch (request.method) {
      case "initialize":
        return { protocolVersion: "2025-06-18", capabilities: { tools: { listChanged: false } }, serverInfo: { name: "nori", version: "0.3.0" } };
      case "ping":
        return {};
      case "tools/list":
        return { tools: TOOLS };
      case "tools/call":
        return this.callTool(String(request.params?.name ?? ""), request.params?.arguments && typeof request.params.arguments === "object" ? request.params.arguments as Record<string, unknown> : {});
      default:
        throw Object.assign(new Error(`Method not found: ${request.method}`), { code: -32601 });
    }
  }
}

export async function serveMcpGateway(options: { url: string; token: string; verbose?: boolean }): Promise<void> {
  const client = new GatewayClient(options.url, options.token, "0.3.0", ["operator.read", "operator.write", "operator.approvals"]);
  await client.connect();
  const bridge = new NoriMcpBridge(client);
  process.stdin.setEncoding("utf8");
  let buffer = "";
  const processLine = async (line: string) => {
    let request: RpcRequest | undefined;
    try {
      request = JSON.parse(line) as RpcRequest;
      if (!request || request.jsonrpc !== "2.0" || typeof request.method !== "string") throw Object.assign(new Error("Invalid JSON-RPC request"), { code: -32600 });
      if (request.id === undefined) return;
      const result = await bridge.handle(request);
      process.stdout.write(`${JSON.stringify({ jsonrpc: "2.0", id: request.id, result })}\n`);
    } catch (error) {
      if (request?.id === undefined) return;
      process.stdout.write(`${JSON.stringify({ jsonrpc: "2.0", id: request.id, error: { code: (error as Error & { code?: number }).code ?? -32603, message: (error as Error).message } })}\n`);
    }
  };
  for await (const chunk of process.stdin) {
    buffer += chunk;
    if (buffer.length > 16 * 1024 * 1024) throw new Error("MCP input frame exceeds 16 MiB");
    while (buffer.includes("\n")) {
      const index = buffer.indexOf("\n");
      const line = buffer.slice(0, index).trim();
      buffer = buffer.slice(index + 1);
      if (line) await processLine(line);
    }
  }
  client.close();
}
